import { Button, CircularProgress, createStyles, List, ListItem, ListItemText, makeStyles, Paper, TextField, Typography } from "@material-ui/core";
import React from "react";
import LocationService, { LocationOption } from "../../services/location/location.service";

const useStyles = makeStyles((theme) =>
    createStyles({
        root: {
            position: "relative"
        },
        input: {
            minWidth: 300,
            [theme.breakpoints.down("sm")]: {
                minWidth: "unset"
            }
        },
        suggestionList: {
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            marginTop: theme.spacing(1),
            zIndex: 4,
            maxHeight: 260,
            overflowY: "auto"
        },
        helperRow: {
            marginTop: theme.spacing(1),
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: theme.spacing(1),
            flexWrap: "wrap"
        },
        error: {
            marginTop: theme.spacing(1),
            color: theme.palette.warning.main
        },
        hint: {
            color: "rgba(255, 255, 255, 0.7)"
        }
    })
);

interface Props {
    placeholder?: string;
    value?: string;
    fullWidth?: boolean;
    onChange?: (value: string) => void;
    onSelect?: (location: LocationOption) => void;
}

const GEOLOCATION_TIMEOUT_MS = 10000;

export default function LocationPickerInput({
    placeholder,
    value,
    fullWidth,
    onChange,
    onSelect
}: Props) {
    const classes = useStyles();
    const [query, setQuery] = React.useState(value || "");
    const [loading, setLoading] = React.useState(false);
    const [geoLoading, setGeoLoading] = React.useState(false);
    const [results, setResults] = React.useState<LocationOption[]>([]);
    const [focused, setFocused] = React.useState(false);
    const [error, setError] = React.useState("");

    React.useEffect(() => {
        setQuery(value || "");
    }, [value]);

    React.useEffect(() => {
        const trimmed = (query || "").trim();
        if (trimmed.length < 3) {
            setLoading(false);
            setResults([]);
            return;
        }

        const controller = new AbortController();
        const timer = window.setTimeout(async () => {
            setLoading(true);
            setError("");

            try {
                const items = await LocationService.search(trimmed, { signal: controller.signal });
                setResults(items);
            } catch (requestError) {
                if (controller.signal.aborted) {
                    return;
                }

                setResults([]);
                setError("Could not search locations right now. You can still type the address manually.");
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        }, 300);

        return () => {
            controller.abort();
            window.clearTimeout(timer);
        };
    }, [query]);

    const handleInputChange = (nextValue: string) => {
        setError("");
        setQuery(nextValue);
        onChange?.(nextValue);
    };

    const handleSelect = (location: LocationOption) => {
        setError("");
        setQuery(location.address);
        setResults([]);
        setFocused(false);
        onChange?.(location.address);
        onSelect?.(location);
    };

    const handleUseCurrentLocation = () => {
        if (!navigator.geolocation) {
            setError("Geolocation is not available in this browser.");
            return;
        }

        setGeoLoading(true);
        setError("");

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords;

                LocationService.reverse(latitude, longitude)
                    .then((location) => {
                        handleSelect({
                            ...location,
                            lat: latitude,
                            lng: longitude
                        });
                    })
                    .catch(() => {
                        setError("Unable to resolve your current location to an address.");
                    })
                    .finally(() => {
                        setGeoLoading(false);
                    });
            },
            () => {
                setGeoLoading(false);
                setError("Location access was denied or unavailable.");
            },
            {
                enableHighAccuracy: true,
                timeout: GEOLOCATION_TIMEOUT_MS,
                maximumAge: 0
            }
        );
    };

    const showSuggestions = focused && results.length > 0;

    return (
        <div className={classes.root}>
            <TextField
                className={classes.input}
                classes={{ root: "lightInput" }}
                InputProps={{ style: { backgroundColor: "#fff" } }}
                fullWidth={fullWidth}
                variant="outlined"
                placeholder={placeholder}
                value={query}
                onChange={(event) => handleInputChange(event.currentTarget.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => {
                    window.setTimeout(() => setFocused(false), 120);
                }}
            />

            <div className={classes.helperRow}>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={handleUseCurrentLocation}
                    disabled={geoLoading}
                >
                    {geoLoading ? "Locating..." : "Use Current Location"}
                </Button>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {loading ? <CircularProgress size={14} color="inherit" /> : null}
                    <Typography variant="caption" className={classes.hint}>
                        Type at least 3 characters to search.
                    </Typography>
                </div>
            </div>

            {showSuggestions ? (
                <Paper className={classes.suggestionList}>
                    <List dense>
                        {results.map((item, index) => (
                            <ListItem
                                key={`${item.lat}_${item.lng}_${index}`}
                                button
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => handleSelect(item)}
                            >
                                <ListItemText primary={item.address} />
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            ) : null}

            {error ? (
                <Typography variant="caption" className={classes.error}>
                    {error}
                </Typography>
            ) : null}
        </div>
    );
}
